import { LegalDoc, type LegalArticle } from "../components/Legal.jsx";
import type { ProductInfo } from "../lib/product.js";
import { useProduct } from "../lib/useProduct.js";

// 現場力 利用規約 初版（2026-09-15）。提供：株式会社アイバックス。
// 049（営業力）：製品名と「本サービス」の説明だけ製品で切り替え、条文は共通（../eigyouryoku/docs/02 決めごと12）。
// 公開前に弁護士等の確認を受けること（内部メモ。画面には出さない）。
// 2026-10-28 改定（#266 1b）：第8条（受領・検収の電子的な確認）を足し、前の第8条〜第25条を第9条〜第26条に繰り下げた。
//   公開した日が 10/28 でなければ、下の updated の改定日をその日に直す（特定商取引法の表記の「第14条」も同じ改定で第13条から直した）
const articlesOf = (P: ProductInfo): LegalArticle[] => [
  {
    heading: "第1条（適用）",
    nodes: [
      {
        ol: [
          "本規約は、本サービスの提供条件及び本サービスの利用に関する当社と利用者との間の一切の関係に適用されます。",
          "当社が本サービスに関して本規約とは別に定める個別規定、ガイドライン、注意書き等（以下「個別規定等」といいます。）は、本規約の一部を構成します。",
          "本規約の定めと個別規定等の定めが異なる場合には、個別規定等の定めが優先して適用されるものとします。",
        ],
      },
    ],
  },
  {
    heading: "第2条（定義）",
    nodes: [
      { p: "本規約において、次の各号に掲げる用語の意味は、当該各号に定めるところによります。" },
      {
        ol: [
          `「本サービス」とは、当社が「${P.name}」の名称で提供する、${P.copy.service}（当社ウェブサイト及びスマートフォン用アプリケーションを含みます。）をいいます。`,
          "「利用者」とは、本規約に同意し、当社所定の方法により利用登録を行った個人又は法人をいいます。",
          "「アカウント」とは、利用者が本サービスを利用するために当社所定の方法により登録した、利用者を識別するための情報の総体をいいます。本サービスでは、原則として1つのメールアドレスにつき1つのアカウントを、1つの事業者に対して発行します。",
          "「登録情報」とは、利用者が利用登録及び本サービスの利用にあたり当社に提供した情報をいいます。",
          "「利用者データ」とは、利用者が本サービスに入力し、又は本サービスを通じて作成・保存したデータ（顧客情報、見積・請求等の各種帳票、これらに含まれる氏名・連絡先等の情報を含みます。）をいいます。",
          "「利用契約」とは、本規約に基づき当社と利用者との間に成立する、本サービスの利用に関する契約をいいます。",
        ],
      },
    ],
  },
  {
    heading: "第3条（利用登録）",
    nodes: [
      {
        ol: [
          "本サービスの利用を希望する者（以下「登録希望者」といいます。）は、本規約に同意のうえ、当社所定の方法により利用登録を申請するものとします。",
          "当社は、当社の基準に従って登録の可否を判断し、当社が登録を認める場合には、その旨を登録希望者に通知します。登録希望者の利用者としての登録は、当社が当該通知を行ったことをもって完了するものとします。",
          "登録希望者は、利用登録にあたり、真実かつ正確な情報を提供するものとします。",
          {
            text: "当社は、登録希望者が次の各号のいずれかに該当すると判断した場合、利用登録を承認せず、又は登録後であっても承認を取り消すことができるものとし、これによって登録希望者に生じた不利益について、当社は責任を負いません。",
            sub: [
              "本規約に違反するおそれがあると当社が判断した場合",
              "当社に提供した情報の全部又は一部につき、虚偽、誤記又は記載漏れがあった場合",
              "過去に本規約違反等により本サービスの利用停止又は登録抹消の措置を受けたことがある場合",
              "第20条（反社会的勢力の排除）に違反すると当社が判断した場合",
              "その他、当社が利用登録を適当でないと判断した場合",
            ],
          },
          "未成年者が本サービスを利用する場合には、親権者その他の法定代理人の同意を得たうえで利用登録を行うものとします。",
        ],
      },
    ],
  },
  {
    heading: "第4条（アカウントの管理）",
    nodes: [
      {
        ol: [
          "利用者は、自己の責任において、本サービスのアカウント（メールアドレス及びパスワードを含みます。）を適切に管理・保管するものとし、これを第三者に利用させ、又は貸与、譲渡、名義変更、売買等をしてはならないものとします。",
          "アカウントの管理不十分、使用上の過誤又は第三者の使用等によって利用者に生じた損害について、当社の故意又は重過失による場合を除き、当社は責任を負いません。",
          "アカウントが第三者に利用されていることが判明した場合、利用者は直ちにその旨を当社に通知するとともに、当社の指示に従うものとします。",
        ],
      },
    ],
  },
  {
    heading: "第5条（登録情報の変更）",
    nodes: [
      {
        ol: [
          "利用者は、登録情報に変更が生じた場合、当社所定の方法により、遅滞なく当該変更内容を届け出るものとします。",
          "前項の届出がなされなかったことにより利用者が不利益を被った場合であっても、当社は責任を負いません。当社が登録情報に記載されたメールアドレス宛てに通知等を発信した場合、その通知等は、通常到達すべき時に利用者へ到達したものとみなします。",
        ],
      },
    ],
  },
  {
    heading: "第6条（料金及び支払方法）",
    nodes: [
      {
        ol: [
          "本サービスには、無料で利用できる機能と、当社が別途定める有料の機能又はプラン（以下「有料プラン」といいます。）があります。無料で利用できる範囲及び有料プランの内容・料金・支払方法は、当社が本サービス上又は当社ウェブサイトに掲示する料金表その他の定めによります。",
          "利用者が有料プランを利用する場合、利用者は、当社が別途定める料金を、当社が指定する方法により支払うものとします。",
          "当社は、料金その他の有料プランの内容を変更することがあります。この場合、当社は、変更内容及び適用開始日を、相当な予告期間をもって本サービス上又は電子メール等により通知します。",
          "利用者が料金の支払を遅滞した場合、利用者は、当社に対し、支払期日の翌日から支払済みまで年14.6％の割合による遅延損害金を支払うものとします。",
          "当社は、法令に別段の定めがある場合又は本規約に定める場合を除き、既に支払われた料金を返金しません。",
        ],
      },
    ],
  },
  {
    heading: "第7条（帳票の内容及び税務・法令の遵守）",
    nodes: [
      {
        ol: [
          "本サービスは、利用者が見積書・請求書その他の帳票を作成・管理することを補助する道具を提供するものであり、作成された帳票の記載内容の正確性、適法性及び有効性を保証するものではありません。",
          "本サービスを通じて作成・発行される帳票の記載内容（金額、消費税額及びその計算、適格請求書（インボイス）としての要件の充足、取引の相手方・内容の正確性を含みます。）並びにこれらに関する会計・税務上の判断及び処理については、利用者が自己の責任において確認し、決定するものとします。",
          "本サービスにより保存される帳票データ及びその電子データ（PDF等を含みます。）の取扱いが、電子帳簿保存法その他の関係法令に定める保存要件を満たすか否かにかかわらず、利用者は、自己に課される帳簿書類の作成・保存義務を、自己の責任において履行するものとします。当社は、当該義務の履行を保証するものではありません。",
        ],
      },
    ],
  },
  {
    // 2026-10-28 改定（#266 1b、docs/96 §9・§14.5）：受領・検収のページ（/a/<鍵>）の記録の扱い。文は Cowork の案のまま（射場さんの確認のあとに公開）
    heading: "第8条（受領・検収の電子的な確認）",
    nodes: [
      {
        ol: [
          "利用者が本サービスから送信した納品書、検収書その他の書類（以下「対象書類」といいます）について、その送信先が本サービスの確認ページで「受領」または「検収」の操作を行ったときは、当社は、その操作の日時、接続元の情報および認証の方法を記録し、利用者および送信先に通知します。",
          "前項の記録は、追記のみで訂正および削除ができない形で保存され、利用者は本サービス上で閲覧およびダウンロードできます。",
          "第1項の操作を受領または検収の確認として扱うかどうか、およびその効果は、利用者と送信先との間の契約によります。利用者は、必要に応じて、あらかじめ送信先とその旨を合意するものとします。",
          "当社は、第1項の記録が送信先本人の操作によるものであることを保証しません。利用者は、送信先のメールアドレスの正確性および確認ページのリンクの管理について責任を負います。",
        ],
      },
    ],
  },
  {
    heading: "第9条（利用者データの取扱い及びバックアップ）",
    nodes: [
      {
        ol: [
          "利用者データに関する権利は、利用者又は正当な権利者に帰属します。当社は、本サービスの提供、維持、改善及び障害対応に必要な範囲に限り、利用者データを取り扱うことができるものとします。",
          "当社は、本サービスの運用のため、合理的な範囲で利用者データのバックアップを行うよう努めますが、利用者データが常に完全に保存・復元されることを保証するものではありません。利用者は、必要に応じ、自己の責任において利用者データの控え（バックアップ）を保存するものとします。",
          "当社は、利用者データのうち氏名・連絡先等の個人情報に該当する情報について、暗号化その他の合理的な安全管理措置を講じるよう努めます。ただし、当社は、当該措置により情報の漏えい、滅失又は毀損が生じないことを保証するものではありません。",
          "当社は、利用者データを統計的に処理し、又は個人を識別できない形に加工したうえで、本サービスの改善その他の目的に利用することがあります。",
        ],
      },
    ],
  },
  {
    heading: "第10条（個人情報及びプライバシー）",
    nodes: [
      {
        ol: [
          "当社は、利用者の個人情報及び利用者データに含まれる個人情報を、当社が別途定める「プライバシーポリシー」に従って適切に取り扱います。",
          "利用者は、利用者データに第三者の個人情報を含めて本サービスに入力する場合、当該個人情報の取得・提供につき、あらかじめ必要な手続（本人の同意の取得を含みます。）を行うものとし、これに関する責任を負うものとします。",
        ],
      },
    ],
  },
  {
    heading: "第11条（禁止事項）",
    nodes: [
      { p: "利用者は、本サービスの利用にあたり、次の各号に掲げる行為をしてはならないものとします。" },
      {
        ol: [
          "法令又は公序良俗に違反する行為",
          "犯罪行為に関連する行為",
          "当社、他の利用者その他の第三者の知的財産権、肖像権、プライバシー、名誉その他の権利又は利益を侵害する行為",
          "本サービスの運営を妨害し、又はそのおそれのある行為",
          "当社のサーバー又はネットワークに過度の負荷をかける行為、不正アクセス行為、これらを試みる行為",
          "本サービスに関し利用しうる情報を改ざんする行為、コンピュータ・ウイルス等を送信する行為",
          "本サービスの全部又は一部を、当社の許諾なく複製、改変、リバースエンジニアリング、逆コンパイル又は逆アセンブルする行為",
          "一つのアカウントを複数人で共用し、又は本サービスを転売、再許諾その他の方法により第三者に利用させる行為",
          "虚偽の情報を登録し、又は他人になりすます行為",
          "反社会的勢力に対して直接又は間接に利益を供与する行為",
          "その他、当社が不適切と合理的に判断する行為",
        ],
      },
    ],
  },
  {
    heading: "第12条（本サービスの提供の停止・中断）",
    nodes: [
      {
        ol: [
          {
            text: "当社は、次の各号のいずれかに該当する場合、利用者に事前に通知することなく、本サービスの全部又は一部の提供を停止又は中断することができるものとします。",
            sub: [
              "本サービスに係るシステムの保守点検又は更新を行う場合",
              "地震、落雷、火災、停電、天災その他の不可抗力により本サービスの提供が困難となった場合",
              "コンピュータ、通信回線等が事故により停止した場合",
              "本サービスの提供に必要な第三者のサービス（サーバー、通信、認証、メール送信等）が停止又は不具合を生じた場合",
              "その他、当社が停止又は中断を必要と合理的に判断した場合",
            ],
          },
          // 2026-10-18 改定（docs/86 §7）：サーバーの更新の再起動の枠
          "毎週日曜日の5:00〜5:30は、メンテナンスのため、本サービスを短時間ご利用いただけない場合があります。",
          "当社は、前2項に基づく本サービスの提供の停止又は中断により利用者又は第三者に生じた損害について、当社の故意又は重過失による場合を除き、責任を負いません。",
        ],
      },
    ],
  },
  {
    heading: "第13条（利用制限及び登録抹消）",
    nodes: [
      {
        ol: [
          {
            text: "当社は、利用者が次の各号のいずれかに該当する場合、事前の通知なく、利用者データの削除、本サービスの利用の制限又は利用者としての登録の抹消をすることができるものとします。",
            sub: [
              "本規約のいずれかの条項に違反した場合",
              "登録情報に虚偽の事実があることが判明した場合",
              "料金の支払を遅滞し、当社の催告後も相当期間内に支払わない場合",
              "一定期間以上にわたり本サービスの利用がなく、当社からの連絡に対して応答がない場合",
              "その他、当社が本サービスの利用を適当でないと合理的に判断した場合",
            ],
          },
          "当社は、本条に基づき当社が行った措置により利用者に生じた損害について、当社の故意又は重過失による場合を除き、責任を負いません。",
        ],
      },
    ],
  },
  {
    heading: "第14条（退会及び退会後のデータの取扱い）",
    nodes: [
      {
        ol: [
          "利用者は、当社所定の方法により、いつでも本サービスから退会し、利用契約を終了することができます。",
          "退会にあたり、利用者が当社に対して負う債務がある場合、利用者は、当社の請求により、当該債務の全額について当然に期限の利益を失い、直ちに当社に弁済するものとします。",
          "当社は、退会後又は登録抹消後、当社が別途定める期間の経過をもって、利用者データを削除することができるものとします。退会後の利用者データの復元・提供には応じられない場合があります。利用者は、退会前に必要なデータを自己の責任で保存するものとします。",
        ],
      },
    ],
  },
  {
    heading: "第15条（本サービスの内容の変更・終了）",
    nodes: [
      {
        ol: [
          "当社は、利用者に事前に通知することなく、本サービスの内容を変更し、又は機能を追加・改善することができるものとします。",
          "当社は、相当な予告期間をもって利用者に通知したうえで、本サービスの全部又は一部の提供を終了することができるものとします。ただし、緊急やむを得ない場合は、この限りではありません。",
          "当社は、本条に基づく本サービスの変更又は終了により利用者に生じた損害について、当社の故意又は重過失による場合を除き、責任を負いません。",
        ],
      },
    ],
  },
  {
    heading: "第16条（保証の否認及び免責）",
    nodes: [
      {
        ol: [
          "当社は、本サービスが利用者の特定の目的に適合すること、期待する機能・正確性・有用性・完全性を有すること、及び本サービスの利用が中断なく継続することを保証するものではありません。",
          "当社は、本サービスに事実上又は法律上の瑕疵（安全性、信頼性、正確性、完全性、有効性、特定目的への適合性、セキュリティ上の欠陥、エラー、バグ、権利侵害等を含みます。）がないことを明示又は黙示に保証するものではありません。",
          "本サービスの利用の結果、利用者と第三者（他の利用者、取引の相手方等）との間で紛争が生じた場合、利用者は、自己の責任と費用においてこれを解決するものとし、当社は当該紛争について責任を負いません。",
        ],
      },
    ],
  },
  {
    heading: "第17条（損害賠償及び責任の制限）",
    nodes: [
      {
        ol: [
          "利用者が本規約に違反し、又は本サービスの利用に関連して当社に損害を与えた場合、利用者は、当社に対し、当該損害（合理的な弁護士費用を含みます。）を賠償するものとします。",
          "当社は、本サービスに関して利用者に生じた損害について、当社の故意又は重過失による場合を除き、責任を負いません。",
          "前項本文にかかわらず、当社が利用者に対して損害賠償責任を負う場合であっても、当社の賠償責任は、当社に故意又は重過失がある場合を除き、利用者に現実に生じた通常かつ直接の損害に限られ、その額は、当該損害の発生時から遡って過去12か月間に利用者が当社に支払った本サービスの利用料金の総額を上限とします。",
          "本サービスの利用に関する当社と利用者との間の契約が消費者契約法に定める消費者契約に該当する場合には、前二項のうち当社の損害賠償責任を全部免除する部分は適用されないものとし、この場合、当社は、当社の軽過失に起因する損害については、前項に定める上限の範囲で賠償責任を負うものとします。",
        ],
      },
    ],
  },
  {
    heading: "第18条（知的財産権）",
    nodes: [
      {
        ol: [
          "本サービス及び本サービスに関連して当社が提供する一切のコンテンツ（プログラム、画面デザイン、ロゴ、文章、帳票の様式・レイアウト等を含みます。）に関する著作権その他の知的財産権は、当社又は正当な権利者に帰属します。",
          "利用者は、当社の事前の書面による承諾なく、前項のコンテンツを複製、改変、頒布、公衆送信その他の方法で利用してはならないものとします。",
          "本条は、利用者が本サービスを用いて作成した利用者データについて、利用者又は正当な権利者の権利を制限するものではありません。",
        ],
      },
    ],
  },
  {
    heading: "第19条（秘密保持）",
    nodes: [
      {
        p: "利用者は、本サービスに関連して当社が秘密である旨を明示して開示した非公開の情報を、当社の事前の書面による承諾なく第三者に開示又は漏えいしてはならず、本サービスの利用の目的以外に使用してはならないものとします。当社の請求があった場合、利用者は、当該情報を速やかに返還又は破棄するものとします。",
      },
    ],
  },
  {
    heading: "第20条（反社会的勢力の排除）",
    nodes: [
      {
        ol: [
          "利用者は、現在及び将来にわたり、暴力団、暴力団員、暴力団準構成員、暴力団関係企業、総会屋、社会運動等標ぼうゴロその他これらに準ずる者（以下「反社会的勢力」といいます。）に該当しないこと、及び反社会的勢力と社会的に非難されるべき関係を有しないことを表明し、保証します。",
          "利用者が前項に違反した場合、当社は、何らの催告を要することなく、本サービスの利用停止又は利用契約の解除をすることができるものとし、これによって利用者に生じた損害について責任を負いません。",
        ],
      },
    ],
  },
  {
    heading: "第21条（権利義務の譲渡の禁止）",
    nodes: [
      {
        ol: [
          "利用者は、当社の事前の書面による承諾なく、利用契約上の地位又は本規約に基づく権利若しくは義務を、第三者に譲渡し、承継させ、又は担保に供してはならないものとします。",
          "当社は、本サービスに係る事業を第三者に譲渡（事業譲渡、会社分割、合併その他の理由による場合を含みます。）する場合には、当該事業譲渡等に伴い、利用契約上の地位、本規約に基づく権利及び義務並びに登録情報その他の利用者に関する情報を当該譲渡の譲受人に承継させることができるものとし、利用者は、あらかじめこれに同意するものとします。",
        ],
      },
    ],
  },
  {
    heading: "第22条（本規約の変更）",
    nodes: [
      {
        ol: [
          {
            text: "当社は、次の各号のいずれかに該当する場合、民法第548条の4の規定に基づき、利用者の個別の同意を要することなく、本規約を変更することができるものとします。",
            sub: [
              "本規約の変更が、利用者の一般の利益に適合するとき",
              "本規約の変更が、契約をした目的に反せず、かつ、変更の必要性、変更後の内容の相当性その他の変更に係る事情に照らして合理的なものであるとき",
            ],
          },
          "当社は、前項による本規約の変更にあたっては、変更後の本規約の内容及び効力発生時期を、効力発生時期の相当期間前までに、本サービス上又は電子メール等により周知します。",
          "利用者が前項の効力発生時期以後に本サービスを利用したときは、変更後の本規約に同意したものとみなします。",
        ],
      },
    ],
  },
  {
    heading: "第23条（通知又は連絡）",
    nodes: [
      {
        ol: [
          "当社から利用者への通知又は連絡は、本サービス上への掲示、登録情報に記載された電子メールアドレス宛ての送信その他当社が適当と判断する方法により行います。",
          "利用者から当社への連絡は、当社が本サービス上又は当社ウェブサイトに定めるお問い合わせ方法によるものとします。",
          "当社は、本サービスの運営上必要な連絡のほか、当社が別途同意を得た場合に、本サービスに関する案内その他の情報を、登録情報に記載された電子メールアドレス宛てに送信することがあります。",
        ],
      },
    ],
  },
  {
    heading: "第24条（分離可能性）",
    nodes: [
      {
        p: "本規約のいずれかの条項又はその一部が、法令等により無効又は執行不能と判断された場合であっても、本規約のその余の規定及び当該条項の残りの部分は、なお有効に存続するものとします。この場合、当社と利用者は、当該無効又は執行不能とされた部分の趣旨を可能な限り実現できるよう、必要な範囲で協議するものとします。",
      },
    ],
  },
  {
    heading: "第25条（準拠法及び管轄裁判所）",
    nodes: [
      {
        ol: [
          "本規約及び利用契約の準拠法は、日本法とします。",
          "本サービス又は本規約に関して当社と利用者との間に生じた紛争については、その訴額に応じ、当社の本店所在地を管轄する地方裁判所又は簡易裁判所を、第一審の専属的合意管轄裁判所とします。",
        ],
      },
    ],
  },
  {
    heading: "第26条（協議解決）",
    nodes: [
      {
        p: "当社及び利用者は、本規約に定めのない事項又は本規約の解釈に疑義が生じた事項については、信義誠実の原則に従い、協議のうえ解決を図るものとします。",
      },
    ],
  },
];

export function TermsPage() {
  const P = useProduct();
  return (
    <LegalDoc
      title={`${P.name} 利用規約`}
      intro={`本利用規約（以下「本規約」といいます。）は、株式会社アイバックス（以下「当社」といいます。）が提供するクラウドサービス「${P.name}」（以下「本サービス」といいます。）の利用条件を定めるものです。本サービスをご利用になる方（第2条に定める「利用者」）は、本規約に同意のうえ本サービスをご利用いただきます。`}
      articles={articlesOf(P)}
      updated="制定日：2026年9月15日／改定日：2026年10月28日"
    />
  );
}
